import type { BoxAnchor, IntervalResult, TrackingInterval } from "../contracts";
import { composeRecovery, recoveryChildren } from "./compose";
import {
  RECOVERY_LIMITS,
  type FrameRecoveryProvider,
  type RecoveryActivity,
  type RecoveryCallOutcome,
  type RecoveryImage,
  type RecoveryIntervalState,
  type RecoveryLineage,
  type RecoveryOptions,
  type RecoveryOutcome,
  type RecoveryOutcomeCounts,
} from "./contracts";
import { frameKey, recoveryCandidate, recoveryIntervalKey, validRecoveryBox } from "./planning";

interface Attempt {
  targetTime: number;
  response: Promise<RecoveryOutcome>;
  outcome?: RecoveryCallOutcome;
}
interface RunCache {
  used: number;
  active: number;
  waiting: Set<() => void>;
  attempts: Map<string, Attempt>;
  listeners: Set<() => void>;
}
// The app supplies one stable provider per original output run/video. No images are retained here.
const runs = new WeakMap<FrameRecoveryProvider, RunCache>();
const runCache = (provider: FrameRecoveryProvider): RunCache => {
  let cache = runs.get(provider);
  if (!cache) {
    cache = { used: 0, active: 0, waiting: new Set(), attempts: new Map(), listeners: new Set() };
    runs.set(provider, cache);
  }
  return cache;
};

function notifyRun(cache: RunCache): void {
  for (const listener of [...cache.listeners]) listener();
}

function outcomeCounts(cache: RunCache | undefined): RecoveryOutcomeCounts {
  const counts = { found: 0, noMatch: 0, failed: 0, pending: cache?.used ?? 0 };
  for (const attempt of cache?.attempts.values() ?? []) {
    if (!attempt.outcome) continue;
    counts[attempt.outcome === "no_match" ? "noMatch" : attempt.outcome]++;
    counts.pending--;
  }
  return counts;
}

export interface RecoveryDependencies {
  extract: (base: IntervalResult, targetTime: number, signal: AbortSignal) => Promise<RecoveryImage[]>;
  refine: (
    parent: TrackingInterval,
    children: readonly TrackingInterval[],
    signal: AbortSignal,
  ) => Promise<IntervalResult[]>;
  /** `final` marks the composed result; the direct publish is a provisional preview. */
  publish: (result: IntervalResult, final: boolean) => void;
  changed: (activity: RecoveryActivity) => void;
}

interface Task {
  base: IntervalResult;
  targetTime: number;
  controller: AbortController;
  started: boolean;
}

export function recoveryAbortError(): Error {
  return new DOMException("Model recovery was cancelled", "AbortError");
}

/** A timeout or cancellation also settles providers that fail to observe their AbortSignal. */
function abortable<T>(promise: Promise<T>, signal: AbortSignal): Promise<T> {
  return new Promise((resolve, reject) => {
    const abort = () => reject(signal.reason ?? recoveryAbortError());
    if (signal.aborted) return abort();
    signal.addEventListener("abort", abort, { once: true });
    promise.then(resolve, reject).finally(() => signal.removeEventListener("abort", abort));
  });
}

/** Also caps overlapping mounts that share the same original run/video provider. */
function requestSlot(cache: RunCache, signal: AbortSignal): Promise<() => void> {
  return new Promise((resolve, reject) => {
    const abort = () => {
      cache.waiting.delete(admit);
      reject(signal.reason ?? recoveryAbortError());
    };
    const admit = () => {
      if (signal.aborted) return abort();
      if (cache.active >= RECOVERY_LIMITS.concurrency) return;
      cache.waiting.delete(admit);
      signal.removeEventListener("abort", abort);
      cache.active++;
      resolve(() => {
        cache.active--;
        for (const waiting of [...cache.waiting]) waiting();
      });
    };
    cache.waiting.add(admit);
    signal.addEventListener("abort", abort, { once: true });
    admit();
  });
}

/** Model work is a separate sidecar. It cannot mutate GT anchors or the retained local results. */
export class RecoveryCoordinator {
  private options: RecoveryOptions = { enabled: false };
  private labels: ReadonlyMap<string, string> = new Map();
  private tasks = new Map<string, Task>();
  private states = new Map<string, RecoveryIntervalState>();
  private playhead = 0;
  private activeRequests = 0;
  private observedCache?: RunCache;
  private readonly cacheChanged = () => this.notify();

  constructor(private readonly dependencies: RecoveryDependencies) {}

  /** Observe the run cache only while this player execution is alive. */
  activate(): void {
    const cache = this.options.provider ? runCache(this.options.provider) : undefined;
    if (this.observedCache === cache) return;
    this.observedCache?.listeners.delete(this.cacheChanged);
    this.observedCache = cache;
    cache?.listeners.add(this.cacheChanged);
  }

  getActivity(): RecoveryActivity {
    const provider = this.options.provider;
    const cache = provider ? runCache(provider) : undefined;
    return {
      enabled: this.options.enabled,
      available: Boolean(provider),
      providerLabel: provider?.label,
      requestsUsed: cache?.used ?? 0,
      maxRequests: RECOVERY_LIMITS.maxRequests,
      concurrency: RECOVERY_LIMITS.concurrency,
      activeRequests: this.activeRequests,
      outcomes: outcomeCounts(cache),
      intervals: this.states,
    };
  }

  configure(options: RecoveryOptions, labels: ReadonlyMap<string, string> = new Map()): boolean {
    this.labels = labels;
    if (this.options.enabled === options.enabled && this.options.provider === options.provider) return false;
    this.cancel();
    this.options = options;
    this.activate();
    this.notify();
    return true;
  }

  cancel(): void {
    this.observedCache?.listeners.delete(this.cacheChanged);
    this.observedCache = undefined;
    const tasks = [...this.tasks.values()];
    this.tasks.clear();
    this.states = new Map();
    tasks.forEach((task) => task.controller.abort(recoveryAbortError()));
    this.notify();
  }

  reconcile(intervals: readonly TrackingInterval[], playhead: number): void {
    this.playhead = playhead;
    const ids = new Set(intervals.map((interval) => interval.id));
    let changed = false;
    for (const [id, task] of this.tasks) {
      if (ids.has(id)) continue;
      this.tasks.delete(id);
      task.controller.abort(recoveryAbortError());
      changed = true;
    }
    if ([...this.states.keys()].some((id) => !ids.has(id))) {
      this.states = new Map([...this.states].filter(([id]) => ids.has(id)));
      changed = true;
    }
    if (changed) this.notify();
    this.pump();
  }

  offer(base: IntervalResult): void {
    const provider = this.options.provider;
    if (!this.options.enabled || !provider || this.states.has(base.interval.id)) return;
    this.activate();
    const candidate = recoveryCandidate(base);
    if (!candidate) return;
    const cache = runCache(provider);
    const previous = cache.attempts.get(recoveryIntervalKey(base.interval));
    if (!previous && cache.used >= RECOVERY_LIMITS.maxRequests) {
      this.states = new Map(this.states).set(base.interval.id, { status: "budget_exhausted" });
      this.notify();
      return;
    }
    const task: Task = {
      base,
      targetTime: previous?.targetTime ?? candidate.timestamp,
      controller: new AbortController(),
      started: false,
    };
    this.tasks.set(base.interval.id, task);
    this.setState(task, { status: "queued", targetTime: task.targetTime });
    this.pump();
  }

  private notify(): void {
    this.dependencies.changed(this.getActivity());
  }

  private current(task: Task): boolean {
    return this.tasks.get(task.base.interval.id) === task && !task.controller.signal.aborted;
  }

  private setState(task: Task, state: RecoveryIntervalState): void {
    if (!this.current(task)) return;
    this.states = new Map(this.states).set(task.base.interval.id, state);
    this.notify();
  }

  private pump(): void {
    if (!this.options.enabled || !this.options.provider) return;
    const running = [...this.tasks.values()].filter((task) => task.started).length;
    const pending = [...this.tasks.values()].filter((task) => !task.started);
    const distance = (task: Task) =>
      Math.max(task.base.interval.start.time - this.playhead, this.playhead - task.base.interval.end.time, 0);
    pending.sort((a, b) => distance(a) - distance(b));
    for (const task of pending.slice(0, RECOVERY_LIMITS.concurrency - running)) {
      task.started = true;
      void this.run(task, this.options.provider);
    }
  }

  private async request(task: Task, provider: FrameRecoveryProvider): Promise<RecoveryOutcome | undefined> {
    const cache = runCache(provider);
    const key = recoveryIntervalKey(task.base.interval);
    const previous = cache.attempts.get(key);
    if (previous) return abortable(previous.response, task.controller.signal);
    if (cache.used >= RECOVERY_LIMITS.maxRequests) {
      this.setState(task, { status: "budget_exhausted", targetTime: task.targetTime });
      return undefined;
    }
    this.setState(task, { status: "extracting", targetTime: task.targetTime });
    const images = await this.dependencies.extract(task.base, task.targetTime, task.controller.signal);
    if (!this.current(task)) throw recoveryAbortError();
    const [target, start, end] = images;
    if (
      !target ||
      !start ||
      !end ||
      images.length !== 3 ||
      frameKey(target.timestamp) !== frameKey(task.targetTime) ||
      images.some(
        (image) =>
          image.sourceWidth !== task.base.decode.sourceWidth || image.sourceHeight !== task.base.decode.sourceHeight,
      )
    )
      throw new Error("Recovery images do not match the original decoded video frames");
    const concurrentAttempt = cache.attempts.get(key);
    if (concurrentAttempt) {
      task.targetTime = concurrentAttempt.targetTime;
      return abortable(concurrentAttempt.response, task.controller.signal);
    }
    const release = await requestSlot(cache, task.controller.signal);
    if (!this.current(task)) {
      release();
      throw recoveryAbortError();
    }
    const admittedAttempt = cache.attempts.get(key);
    if (admittedAttempt) {
      release();
      task.targetTime = admittedAttempt.targetTime;
      return abortable(admittedAttempt.response, task.controller.signal);
    }
    // Another request may have consumed the remaining budget while this extraction was queued.
    if (cache.used >= RECOVERY_LIMITS.maxRequests) {
      release();
      this.setState(task, { status: "budget_exhausted", targetTime: task.targetTime });
      return undefined;
    }
    cache.used++;
    this.activeRequests++;
    this.setState(task, { status: "requesting", targetTime: task.targetTime });
    const timeout = setTimeout(
      () => task.controller.abort(new Error("Model recovery timed out after 60 seconds")),
      RECOVERY_LIMITS.timeoutMs,
    );
    const attempt: Attempt = {
      targetTime: task.targetTime,
      response: abortable(
        Promise.resolve().then(() =>
          provider.recover(
            {
              interval: task.base.interval,
              trackLabel: this.labels.get(task.base.interval.trackId),
              target,
              references: [
                { anchor: task.base.interval.start, image: start },
                { anchor: task.base.interval.end, image: end },
              ],
            },
            task.controller.signal,
          ),
        ),
        task.controller.signal,
      ),
    };
    attempt.response = attempt.response
      .then((outcome) => {
        if (outcome.status !== "found" && outcome.status !== "no_match") {
          throw new Error("Model recovery returned an invalid outcome");
        }
        if (outcome.status === "found" && !validRecoveryBox(outcome.box)) {
          throw new Error("Model recovery returned an invalid normalized box");
        }
        attempt.outcome = outcome.status;
        notifyRun(cache);
        return outcome;
      })
      .catch((error: unknown) => {
        attempt.outcome = "failed";
        notifyRun(cache);
        throw error;
      });
    // Retain only the settled answer (or failure); a rerun must never spend again on this GT pair.
    cache.attempts.set(key, attempt);
    notifyRun(cache);
    try {
      return await attempt.response;
    } finally {
      clearTimeout(timeout);
      release();
      this.activeRequests--;
      this.notify();
    }
  }

  private async run(task: Task, provider: FrameRecoveryProvider): Promise<void> {
    let outcome: RecoveryOutcome | undefined;
    let direct: IntervalResult | undefined;
    try {
      outcome = await this.request(task, provider);
      if (!outcome || !this.current(task)) return;
      const metadata = {
        outcome: outcome.status,
        targetTime: task.targetTime,
        requestId: outcome.requestId,
        usage: outcome.usage,
        timings: outcome.timings,
      };
      if (outcome.status === "no_match") {
        this.setState(task, { ...metadata, status: "no_match" });
        return;
      }
      const anchor: BoxAnchor = {
        id: `${task.base.interval.id}::model:${frameKey(task.targetTime)}`,
        time: task.targetTime,
        coordinates: outcome.box,
      };
      const lineage: RecoveryLineage = {
        anchorTime: anchor.time,
        provider: provider.label,
        requestId: outcome.requestId,
      };
      direct = composeRecovery(task.base, anchor, lineage);
      this.setState(task, { ...metadata, status: "refining" });
      this.dependencies.publish(direct, false);
      const children = await this.dependencies.refine(
        task.base.interval,
        recoveryChildren(task.base.interval, anchor),
        task.controller.signal,
      );
      if (!this.current(task)) return;
      const composed = composeRecovery(task.base, anchor, lineage, children);
      this.dependencies.publish(composed, true);
      this.setState(task, {
        ...metadata,
        status: "recovered",
        recoveredFrames: composed.frames.filter((frame, index) => frame !== task.base.frames[index] && frame.box)
          .length,
      });
    } catch (error) {
      // A timeout aborts the request, but should remain a visible terminal error. Other cancellations disappear.
      if (this.tasks.get(task.base.interval.id) !== task) return;
      this.states = new Map(this.states).set(task.base.interval.id, {
        status: "failed",
        outcome: outcome?.status ?? runCache(provider).attempts.get(recoveryIntervalKey(task.base.interval))?.outcome,
        targetTime: task.targetTime,
        requestId: outcome?.requestId,
        usage: outcome?.usage,
        timings: outcome?.timings,
        recoveredFrames: direct?.frames.filter((frame) => frame.method === "model-recovery").length,
        error: error instanceof Error ? error.message : String(error),
      });
      this.notify();
    } finally {
      if (this.tasks.get(task.base.interval.id) === task) this.tasks.delete(task.base.interval.id);
      // A failed child must release its sibling's queued admission as well.
      task.controller.abort(recoveryAbortError());
      this.pump();
    }
  }
}
